<template>
  <SbLoadingPromiseWrapper
    :loading-promise="loadingPromise"
    :classes="['loading-wrapper', 'dashboard-notifications']"
  >
    <template v-if="hasNotifications">
      <notifications :notifications="notifications">
        <div
          :key="notification.NotificationId"
          slot-scope="{ notification }"
          class="notifications-wrapper"
        >
          <SbLoadingPromiseWrapper
            :loading-promise="loadingNotification(notification.NotificationId)"
          >
            <notification>
              <p slot="date" class="date">
                {{ notification.CreatedAt | date($t('Today')) }}
              </p>
              <p class="content">
                {{ $t('NotificationTexts[' + notification.NotificationTypeId + ']', {AssociationId: notification.AssociationId}) }}
              </p>
              <div slot="actions" class="actions">
                <sb-button
                  v-if="notification.ResolveUrl != null"
                  :variation="notification.Resolvable ? 'warning' : 'primary'"
                  size="small"
                  type="a"
                  :href="notification.ResolveUrl"
                  class="action"
                  @click="notificationAction(notification)"
                >
                  {{ notification.Resolvable ? $t('Resolve') : $t('View') }}
                </sb-button>
                <a class="action" href="#" @click.prevent="dismissNotification(notification)">{{ $t('Dismiss') }}</a>
              </div>
            </notification>
          </SbLoadingPromiseWrapper>
        </div>
      </notifications>
      <fade-transition mode="out-in">
        <template v-if="loadable && !fetchedAll">
          <IntersectionObserver :threshold="1" @intersect-enter="handleEnter">
            <sb-button v-if="loadable" @click.prevent="handleLoadNotifications">
              Load More
            </sb-button>
          </IntersectionObserver>
        </template>
        <template v-if="loadable && fetchedAll">
          <p class="notifications-terminator">
            End of List
          </p>
        </template>
      </fade-transition>
    </template>
    <template v-else>
      <div class="empty">
        <div class="empty-panel-image">
          <img :src="emptyPanelImagePath">
        </div>
        <div class="empty-panel-text">
          <h4>{{ $t(title) }}</h4>
          <p>{{ $t(subText) }}</p>
        </div>
      </div>
    </template>
  </SbLoadingPromiseWrapper>
</template>

<script>
import moment from 'moment'
import debounce from 'lodash/debounce'

import Notifications from './Notifications'
import Notification from './Notification'

import NotificationTypeEnum from '@/enums/modules/dashboard/notificationType'
import localDateTime from '@/filters/localDateTime'
import { getTelemetryInstance } from '@/telemetry'

const STORE_MODULE = 'dashboard/notifications/'

export default {
  name: 'DashboardNotifications',
  components: {
    Notifications,
    Notification
  },
  mixins: [],
  props: {
    pageSize: {
      type: Number,
      default: 3
    },
    title: {
      type: String,
      default: 'EmptyPanelMainText'
    },
    subText: {
      type: String,
      default: 'EmptyPanelSubText'
    },
    loadable: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    emptyPanelImagePath: 'https://uiassets-afd.shipbob.com/legacyuiassets/webassets/dashboard/images/notifications_panel_empty.svg'
  }),
  computed: {
    notifications () {
      return this.$shipBobStore.getters['dashboard/notifications/notifications']
    },
    hasNotifications () {
      return this.notifications.length > 0
    },
    fetchedAll () {
      return this.$shipBobStore.getters['dashboard/notifications/fetchedAllNotifications']
    },
    loadingPromise () {
      return this.$shipBobStore.getters['dashboard/notifications/loading']('initialBufferingNotifications')
    }
  },
  watch: {},
  mounted () {
    this.loadNotifications()
  },
  destroyed () {},
  methods: {
    handleEnter () {
      this.loadNotifications()
    },
    handleLoadNotifications: debounce(function () {
      // eslint-disable-next-line no-invalid-this
      this.loadNotifications()
    }, 500),
    loadNotifications () {
      this.$shipBobStore.dispatch(`${STORE_MODULE}loadNotifications`, this.pageSize)
    },
    async dismissNotification (notification) {
      getTelemetryInstance()?.trackEvent({
        name: 'MERCHANT_APP_DASHBOARD_NOTIFICATION_DISMISSED',
        properties: {
          ...notification
        }
      })
      await this.$shipBobStore.dispatch(`${STORE_MODULE}dismissNotification`, notification)
    },
    loadingNotification (notificationId) {
      return this.$shipBobStore.getters['dashboard/notifications/loading'](notificationId)
    },
    notificationAction (notification) {
      getTelemetryInstance()?.trackEvent({
        name: 'MERCHANT_APP_DASHBOARD_NOTIFICATION_CLICKED',
        properties: {
          ...notification
        }
      })
    }
  },
  directives: {},
  filters: {
    date (time, todayText) {
      const localTime = localDateTime.filter(time, 'MM/DD/YYYY', false)
      const date = moment(localTime).format('MM/DD/YY')
      const today = moment().format('MM/DD/YY')
      return (date === today) ? todayText : date
    }
  },
  i18n: {
    messages: {
      'en-US': {
        ErrorMessage: 'Hm... we couldn\'t load your notifications. Please refresh your browser and if you have issues, let our support team know.',
        EmptyPanelMainText: 'Welcome back!', // 'Welcome back, {firstName}!': getUser doesn't seem to have firstName, need to look into that
        EmptyPanelSubText: 'You\'re all caught up! Keep up the great work, and remember to relax a bit :)',
        Resolve: 'Resolve',
        Dismiss: 'Dismiss',
        View: 'View',
        Today: 'Today',
        NotificationTexts: {
          [NotificationTypeEnum.WROComplete]: 'Receiving completed: your inventory from Warehouse Receiving Order #{AssociationId} is now fulfillable',
          [NotificationTypeEnum.UROOnHold]: 'New On-hold Receiving item #{AssociationId}: take action to avoid further receiving delays and extra fees',
          [NotificationTypeEnum.PaymentFailed]: 'Billing Error: Please update your payment method to avoid shipping delays!'
        }
      }
    }
  }
}
</script>

<style scoped lang='scss'>
.dashboard-notifications {
  position: relative;
  display: flex;
  flex-direction: column;
  .notifications-wrapper {
    position: relative;
    border-bottom: 1px solid #eee;
    &:last-child {
      border-bottom: none;
    }
  }
  p { margin: 0; }
  .date {
    color: #adadad;
  }
  .content {}
  .actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    .action {
      margin-right: 1em;
      &:last-child {
        margin-right: 0;
      }
    }
  }
  .empty {
    text-align: center;
  }
  .notifications-terminator {
    text-align: center;
    color: #adadad;
  }
}
</style>
